import {observer} from 'mobx-react-lite'
import React, {
  CSSProperties,
  ForwardedRef,
  forwardRef,
  ReactElement,
  useEffect,
  useState
} from 'react'
import {Util} from 'yougile-util'
import {DroppableItem, useDnDStore} from '@components/draggable'
import {cloneElementWithRef, updateRef} from '@containers/element-utils'

type ReactText = string | number
type ReactNodeArray = Array<ReactNode>
type ReactNode =
  | ReactElement
  | ReactText
  | ReactNodeArray
  | boolean
  | null
  | undefined

export interface DroppableProps
  extends Pick<
    DroppableItem,
    | 'accepts'
    | 'onDragOver'
    | 'onDragEnter'
    | 'onDragLeave'
    | 'onDrop'
    | 'container'
    | 'showPlaceholder'
  > {
  children?: ReactNode
  disabled?: boolean
  className?: string
  style?: CSSProperties
}

export type UseDroppableRef = Pick<
  DroppableProps,
  | 'accepts'
  | 'onDragEnter'
  | 'onDragLeave'
  | 'onDragOver'
  | 'onDrop'
  | 'container'
  | 'disabled'
  | 'showPlaceholder'
>

type UseDroppableRefRet = {
  setElement: (el: HTMLElement | null) => void
  element: HTMLElement | null
}

export const useDroppableRef = ({
  accepts,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
  container,
  disabled,
  showPlaceholder
}: UseDroppableRef): UseDroppableRefRet => {
  const [id] = useState(Util.UUID())
  const [element, setElement] = useState<HTMLElement | null>()
  const [, store] = useDnDStore()

  useEffect(() => {
    if (!element || disabled) {
      return undefined
    }

    store.addDroppable({
      id,
      element,
      accepts,
      onDragEnter,
      onDragLeave,
      onDragOver,
      onDrop,
      container,
      showPlaceholder
    })

    return () => store.removeDroppable(id)
  }, [
    disabled,
    element,
    onDragEnter,
    onDragLeave,
    onDragOver,
    onDrop,
    container,
    showPlaceholder
  ])

  return {element, setElement}
}

export const Droppable = observer(
  ({children, className, style, ...props}: DroppableProps) => {
    const {setElement: ref} = useDroppableRef(props)
    return (
      <div ref={ref} className={className} style={style}>
        {children}
      </div>
    )
  }
)

export type WithDroppableProps<P> = {
  children: React.FunctionComponentElement<P> | false | null | undefined
} & Omit<DroppableProps, 'children' | 'className' | 'style'>

export const WithDroppable = observer(
  forwardRef(
    <T extends HTMLElement, P extends {ref?: React.Ref<T>}>(
      {children, ...props}: WithDroppableProps<P>,
      forwardedRef: ForwardedRef<HTMLElement>
    ) => {
      const {setElement: setDroppableEl, element} = useDroppableRef(props)

      return children
        ? cloneElementWithRef(children, (el: HTMLElement) => {
            if (!el || el === element) {
              return
            }
            updateRef(el, setDroppableEl)
            updateRef(el, forwardedRef)
          })
        : null
    }
  )
)
